import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const MOBILE_LIGHTS_RATIO = 0.5;
const LIGHTS_COUNT = 30;
const GLOBAL_ALPHA = 0.6;
const HALO_RADIUS_RATIO = 5;

const BEAMS_COUNT = 12;
const BEAM_WIDTH_MULTIPLIER = 1.6;
const BEAM_FAN_HALF_ANGLE = 0.85;
const BEAM_FADE_STOPS = { mid: 0.35, far: 0.7 };
const LIGHT_BEAM_FADE_STOPS = { mid: 0.55, far: 0.88 };

export type RisingLightsAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type LightParticle = {
  x: number;
  y: number;
  radius: number;
  riseSpeed: number;
  swayAmplitude: number;
  swaySpeed: number;
  swayPhase: number;
  flickerSpeed: number;
  flickerPhase: number;
  depth: number;
};

type LightBeam = {
  baseAngle: number;
  halfWidth: number;
  weight: number;
  phase: number;
  shimmerSpeed: number;
  driftSpeed: number;
  driftAmplitude: number;
};

export const RisingLightsAnimation: FC<RisingLightsAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function flameCoreColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
        case "light":
          return `hsla(${hue}, 55%, 58%, ${alpha * 0.85})`;
        case "darkdull":
          return `hsla(0, 0%, 82%, ${alpha * 0.78})`;
        case "lightdull":
          return `hsla(0, 0%, 38%, ${alpha * 0.72})`;
        default:
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
      }
    }

    function haloColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
        case "light":
          return `hsla(${hue}, 55%, 78%, ${alpha * 0.28})`;
        case "darkdull":
          return `hsla(0, 0%, 88%, ${alpha * 0.15})`;
        case "lightdull":
          return `hsla(0, 0%, 50%, ${alpha * 0.22})`;
        default:
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
      }
    }

    function haloEdgeColor(): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, 0)`;
        case "light":
          return `hsla(${hue}, 55%, 78%, 0)`;
        case "darkdull":
          return `hsla(0, 0%, 88%, 0)`;
        case "lightdull":
          return `hsla(0, 0%, 50%, 0)`;
        default:
          return `hsla(${hue}, 80%, 88%, 0)`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    const lightsCount = isMobile ? Math.round(LIGHTS_COUNT * MOBILE_LIGHTS_RATIO) : LIGHTS_COUNT;
    const lights: LightParticle[] = Array.from({ length: lightsCount }, () =>
      mkLight(canvasEl.width, canvasEl.height, false)
    );
    const beams: LightBeam[] = mkBeams();

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, lights);
    }

    window.addEventListener("resize", onResize);

    const isDarkDull = theme === "darkdull";
    const beamFadeStops = theme === "light" ? LIGHT_BEAM_FADE_STOPS : BEAM_FADE_STOPS;
    let frame = 0;

    function fillWedge(originX: number, originY: number, angle: number, halfWidth: number, length: number): void {
      context.beginPath();
      context.moveTo(originX, originY);
      context.lineTo(originX + Math.cos(angle - halfWidth) * length, originY + Math.sin(angle - halfWidth) * length);
      context.lineTo(originX + Math.cos(angle + halfWidth) * length, originY + Math.sin(angle + halfWidth) * length);
      context.closePath();
      context.fill();
    }

    function drawBeams(): void {
      const width = canvasEl.width;
      const height = canvasEl.height;
      const originX = width * 0.5;
      const originY = -height * 0.25;
      const length = Math.hypot(width, height) * 1.9;

      const radiance = context.createRadialGradient(
        originX,
        originY,
        0,
        originX,
        originY,
        Math.hypot(width, height) * 0.8
      );
      radiance.addColorStop(0, haloColor(isDarkDull ? 1.6 : 1.3));
      radiance.addColorStop(1, haloEdgeColor());
      context.fillStyle = radiance;
      context.fillRect(0, 0, width, height);

      for (const beam of beams) {
        const angle = beam.baseAngle + Math.sin(frame * beam.driftSpeed + beam.phase) * beam.driftAmplitude;
        const shimmer = beam.weight * (0.55 + 0.45 * Math.sin(frame * beam.shimmerSpeed + beam.phase));
        const alpha = isDarkDull ? shimmer * 1.4 + 0.1 : shimmer;

        const travel = (height - originY) / Math.max(0.35, Math.sin(angle));
        const fadeX = originX + Math.cos(angle) * travel;
        const fadeY = originY + Math.sin(angle) * travel;
        const halfWidth = beam.halfWidth * BEAM_WIDTH_MULTIPLIER;

        const glow = context.createLinearGradient(originX, originY, fadeX, fadeY);
        glow.addColorStop(0, haloColor(alpha * 2.1));
        glow.addColorStop(beamFadeStops.mid, haloColor(alpha * 0.95));
        glow.addColorStop(beamFadeStops.far, haloColor(alpha * 0.25));
        glow.addColorStop(1, haloEdgeColor());
        context.fillStyle = glow;
        fillWedge(originX, originY, angle, halfWidth, length);

        const core = context.createLinearGradient(originX, originY, fadeX, fadeY);
        core.addColorStop(0, flameCoreColor(alpha * 0.3));
        core.addColorStop(beamFadeStops.mid, flameCoreColor(alpha * 0.12));
        core.addColorStop(beamFadeStops.far, flameCoreColor(alpha * 0.03));
        core.addColorStop(1, haloEdgeColor());
        context.fillStyle = core;
        fillWedge(originX, originY, angle, halfWidth * 0.4, length);
      }
    }

    function drawLights(frameSteps: number): void {
      for (const light of lights) {
        light.y -= light.riseSpeed * frameSteps;
        light.x += Math.sin(frame * light.swaySpeed + light.swayPhase) * light.swayAmplitude * 0.25 * frameSteps;

        const flicker = 0.75 + 0.25 * Math.sin(frame * light.flickerSpeed + light.flickerPhase);
        let alpha = GLOBAL_ALPHA * light.depth * flicker;
        if (isDarkDull) {
          alpha = alpha * 1.5 + 0.12;
        }
        alpha = Math.min(isDarkDull ? 1 : 0.85, alpha);

        const haloRadius = light.radius * HALO_RADIUS_RATIO;
        const halo = context.createRadialGradient(light.x, light.y, 0, light.x, light.y, haloRadius);
        halo.addColorStop(0, haloColor(alpha * 2.2));
        halo.addColorStop(0.5, haloColor(alpha * 0.7));
        halo.addColorStop(1, haloEdgeColor());
        context.fillStyle = halo;
        context.beginPath();
        context.arc(light.x, light.y, haloRadius, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = flameCoreColor(alpha);
        context.beginPath();
        context.arc(light.x, light.y, light.radius * 0.75, 0, Math.PI * 2);
        context.fill();

        if (light.y + haloRadius < 0) {
          Object.assign(light, mkLight(canvasEl.width, canvasEl.height, true));
        }
      }
    }

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      frame += frameSteps;
      drawBeams();
      drawLights(frameSteps);
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};

function mkLight(width: number, height: number, spawnBelowViewport: boolean): LightParticle {
  const radius = 2.2 + Math.random() * 3.6;
  return {
    x: Math.random() * width,
    y: spawnBelowViewport ? height + radius * 6 + Math.random() * height * 0.3 : Math.random() * height,
    radius,
    riseSpeed: 0.12 + Math.random() * 0.26,
    swayAmplitude: 0.3 + Math.random() * 0.9,
    swaySpeed: 0.006 + Math.random() * 0.014,
    swayPhase: Math.random() * Math.PI * 2,
    flickerSpeed: 0.03 + Math.random() * 0.05,
    flickerPhase: Math.random() * Math.PI * 2,
    depth: 0.45 + Math.random() * 0.55,
  };
}

function mkBeams(): LightBeam[] {
  const fanStart = Math.PI / 2 - BEAM_FAN_HALF_ANGLE;
  const fanEnd = Math.PI / 2 + BEAM_FAN_HALF_ANGLE;
  return Array.from({ length: BEAMS_COUNT }, (_unused, index) => {
    const position = (index + 0.5) / BEAMS_COUNT;
    return {
      baseAngle: fanStart + (fanEnd - fanStart) * position + (Math.random() - 0.5) * 0.04,
      halfWidth: 0.02 + Math.random() * 0.038,
      weight: 0.5 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
      shimmerSpeed: 0.006 + Math.random() * 0.014,
      driftSpeed: 0.0016 + Math.random() * 0.0022,
      driftAmplitude: 0.004 + Math.random() * 0.006,
    };
  });
}
